import { faSearch, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import debounce from 'debounce-promise';
import type { IObservableArray } from 'mobx';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select';
import { components } from 'react-select';

import type {
  Campaign,
  GeoFilter as GeoFilterModel,
  IGeoFilter,
  Targetable,
  Targeting,
} from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { AsyncSelect, Button, Radios, Select, Well } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { cssVar } from '@feathr/hooks';

import GeoFilterV1 from './GeoFilterV1';

import * as styles from './GeoFilter.css';

interface IProps {
  campaign: Campaign;
  targeting: Targeting;
  targetings: IObservableArray<Targeting>;
  onRemove: (targeting: Targeting) => void;
  layout?: 'stacked' | 'inline';
}

interface IFilters {
  is_archived__ne: boolean;
  name__icontains?: string;
  kind__in?: string[];
}

interface ITypeOption {
  id: string;
  name: string;
}

const TYPE_OPTIONS: ITypeOption[] = [
  { id: 'all', name: 'All types' },
  { id: 'Country', name: 'Countries' },
  { id: 'Region', name: 'Regions' },
  { id: 'City', name: 'Cities' },
];

function renderOptionOrSingleValue(
  props: SingleValueProps<IGeoFilter> | OptionProps<IGeoFilter>,
): JSX.Element {
  const { data } = props;

  return (
    <div className={styles.option}>
      <span className={styles.label}>{data.name}</span>
      <dl className={styles.data}>
        {['City'].includes(data.kind) && (
          <>
            <dt className={'visuallyhidden'}>Region</dt>
            <dd>{data.address.region},</dd>
          </>
        )}
        {['Region', 'City'].includes(data.kind) && (
          <>
            <dt className={'visuallyhidden'}>Country</dt>
            <dd>{data.address.country}</dd>
          </>
        )}
        <dt className={'visuallyhidden'}>Kind</dt>
        <dd></dd>
      </dl>
    </div>
  );
}

function Option(props: OptionProps<IGeoFilter>): JSX.Element {
  return (
    <components.Option {...props} className={styles.wrapper}>
      {renderOptionOrSingleValue(props)}
    </components.Option>
  );
}

function SingleValue(props: SingleValueProps<IGeoFilter>): JSX.Element {
  return (
    <components.SingleValue {...props} className={styles.wrapper}>
      {renderOptionOrSingleValue(props)}
    </components.SingleValue>
  );
}

function GeoFilter({
  campaign,
  targeting,
  targetings,
  onRemove,
  layout = 'inline',
}: Readonly<IProps>): JSX.Element {
  const { GeoFilters, Targetables } = useStore();

  const { t } = useTranslation();

  let targetable: Targetable | undefined;
  const targetableId = targeting.get('target_data');
  if (targetableId) {
    targetable = Targetables.get(targetableId);
  }
  let geofilter: GeoFilterModel | undefined;
  if (targetable?.get('geo_filter')) {
    geofilter = GeoFilters.get(targetable.get('geo_filter')!);
  }
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<ITypeOption>(TYPE_OPTIONS[0]);

  const loadOptionsInternal = useCallback(
    async (inputValue: string): Promise<IGeoFilter[]> => {
      setIsLoading(true);
      const filters: IFilters = {
        is_archived__ne: true,
      };

      if (inputValue) {
        filters.name__icontains = inputValue;
      }

      if (selectedType.id !== 'all') {
        filters.kind__in = [selectedType.id];
      }

      const geoFilters = GeoFilters.list({
        filters,
        pagination: {
          page: 0,
          page_size: 50,
        },
      });
      await when(() => !geoFilters.isPending);
      setIsLoading(false);

      // Get all used geofilter IDs from existing targetings
      const usedGeoFilterIds = new Set<string>();
      targetings.forEach((t) => {
        const targetableId = t.get('target_data');
        if (targetableId) {
          const targetable = Targetables.get(targetableId);
          if (targetable?.get('geo_filter')) {
            usedGeoFilterIds.add(targetable.get('geo_filter')!);
          }
        }
      });

      return [
        ...geoFilters.models.filter((gf) => !usedGeoFilterIds.has(gf.id)).map((gf) => gf.toJS()),
      ];
    },
    [GeoFilters, Targetables, targetings, selectedType],
  );

  const loadOptions = useMemo(() => debounce(loadOptionsInternal, 300), [loadOptionsInternal]);

  const handleIncludedChange = useCallback(
    (newValue?: string): void => {
      targeting.set({ included: newValue === 'included' });
    },
    [targeting],
  );

  const handleTypeChange = useCallback((option?: ITypeOption): void => {
    if (option) {
      setSelectedType(option);
    }
  }, []);

  const handleClear = useCallback((): void => {
    targeting.set({
      name: '',
      target_data: undefined,
    });
  }, [targeting]);

  const handleRemove = useCallback((): void => {
    onRemove(targeting);
  }, [onRemove, targeting]);

  const handleSelectSingle = useCallback(
    (option: IGeoFilter): void => {
      const newGeoTargetable = Targetables.create({
        _cls: TargetableClass.geo,
        name: option.name,
        geo_filter: option.id,
        partner: campaign.get('parent_kind') === 'partner' ? campaign.get('parent') : undefined,
      });
      targeting.set({
        name: option.name,
        target_data: newGeoTargetable.get('id'),
      });
    },
    [Targetables, campaign, targeting],
  );

  function getGeoFilterValue(geofilter: GeoFilterModel | undefined): IGeoFilter | undefined {
    if (!geofilter) {
      return undefined;
    }
    return !geofilter.isPending ? geofilter.toJS() : undefined;
  }

  const cacheKey = `geo-filter-${targetings.length}`;

  return (
    <Well width={'full'}>
      <Flex
        className={`${styles.mainContainer} ${layout === 'inline' ? styles.inline : ''}`}
        direction={layout === 'stacked' ? 'column' : 'row'}
        gap={cssVar('--spacing-3')}
      >
        <Flex
          align={'center'}
          className={styles.topContainer}
          direction={'row'}
          gap={cssVar('--spacing-2')}
        >
          <AsyncSelect<IGeoFilter>
            cacheOptions={true}
            className={styles.wrapper}
            components={{ Option, SingleValue }}
            defaultOptions={true}
            isClearable={true}
            isLoading={isLoading}
            key={cacheKey}
            loadOptions={loadOptions}
            name={'geo-filter'}
            onClear={handleClear}
            onSelectSingle={handleSelectSingle}
            placeholder={t('Start typing a location...')}
            prefix={<FontAwesomeIcon icon={faSearch} />}
            value={getGeoFilterValue(geofilter)}
            wrapperClassName={styles.selectWrapper}
          />
          <Select<ITypeOption>
            className={styles.typeSelect}
            name={'geo-filter-type'}
            onSelectSingle={handleTypeChange}
            options={TYPE_OPTIONS}
            value={selectedType}
            wrapperClassName={styles.typeSelectWrapper}
          />
        </Flex>
        <Flex
          align={'center'}
          className={`${styles.bottomContainer} ${layout === 'inline' ? styles.inline : ''}`}
          direction={'row'}
          gap={cssVar('--spacing-2')}
          justify={layout === 'inline' ? 'flex-start' : 'space-between'}
        >
          <Radios
            className={styles.radiosWrapper}
            layout={'block'}
            onChange={handleIncludedChange}
            options={[
              { id: 'included', name: 'Included', theme: 'green' },
              { id: 'excluded', name: 'Excluded', theme: 'red' },
            ]}
            value={targeting.get('included') ? 'included' : 'excluded'}
          />
          <Button onClick={handleRemove} type={'iconDanger'}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Flex>
      </Flex>
    </Well>
  );
}

GeoFilter.V1 = GeoFilterV1;
export default observer(GeoFilter);
